// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { DateTime } from 'luxon';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import Icon from '../../../icon/Icon';
import PopoverHelper from '../../../popover/PopoverHelper';
import DateTimePicker from '../../../dateTimePicker/DateTimePicker';
import { Button } from '@/common-utils/components/ui/button';
// Common
import ICONS from '@/common-utils/enums/icons';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
// Sytle
import './DateFilter.scss';

/**
 *
 * @param root0
 * @param root0.isLocalDateAndTime
 * @param root0.isRestricted
 * @param root0.addFilter
 * @param root0.columnId
 * @param root0.resetThisFilter
 * @param root0.setLaunchSearch
 * @returns {JSX} DateFilter
 */
function DateFilter({
    isLocalDateAndTime,
    isRestricted,
    addFilter,
    columnId,
    resetThisFilter,
    setLaunchSearch
}) {

    // State
    const [isDirty, setIsDirty] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(true);
    const [dateTimeValues, setDateTimeValues] = useState(undefined);
    const [resetSelection, setResetSelection] = useState(false);

    // Hooks
    useEffect(() => {
        if (resetThisFilter && isDirty) {
            setDateTimeValues(undefined);
            setResetSelection(false);
            setIsDirty(false);
        }
    }, [resetThisFilter]);

    useEffect(() => {
        if (resetSelection) {
            setResetSelection(false);
        }
    }, [resetSelection]);

    // Functions
    const updateFilter = () => {
        if (dateTimeValues && (dateTimeValues.startDateSelected || dateTimeValues.endDateSelected)) {
            addFilter(
                columnId,
                {
                    startDateSelected: dateTimeValues.startDateSelected,
                    endDateSelected: dateTimeValues.endDateSelected,
                    beforeSelected: dateTimeValues.beforeSelected,
                    afterSelected: dateTimeValues.afterSelected,
                    startTimeSelected: dateTimeValues.startTimeSelected,
                    endTimeSelected: dateTimeValues.endTimeSelected
                });
            setIsDirty(true);
            setLaunchSearch(true);
        }
    };

    const onOpenChange = () => {
        if (isCalendarOpen) {
            setDateTimeValues(undefined);
        }
        setIsCalendarOpen(!isCalendarOpen);
    };

    const renderDateRange = () => {
        if (dateTimeValues && dateTimeValues.startDateSelected && dateTimeValues.endDateSelected) {
            if (dateTimeValues.beforeSelected) {
                return `${getTranslatedText('date_picker.before_date')} ${DateTime.fromISO(dateTimeValues.endDateSelected).toFormat('D')}`;
            } else if (dateTimeValues.afterSelected) {
                return `${getTranslatedText('date_picker.after_date')} ${DateTime.fromISO(dateTimeValues.startDateSelected).toFormat('D')}`;
            } else {
                return `${DateTime.fromISO(dateTimeValues.startDateSelected).toFormat('D')} - ${DateTime.fromISO(dateTimeValues.endDateSelected).toFormat('D')}`;
            }
        }
        return <Icon icon={ICONS.CALENDAR} />;
    };


    // Render
    return (
        <PopoverHelper
            isOpen={isCalendarOpen}
            onOpenChange={onOpenChange}
            asChild
            trigger={
                <Button
                    variant={'outline'}
                    className={cn(
                        'pl-3 text-left font-normal rounded-sm  min-w-60 h-10',
                        !dateTimeValues && 'text-muted-foreground'
                    )}
                    dataCy='date-filter-btn'
                >
                    {renderDateRange()}
                </Button>
            }
            content={
                <div className='flex flex-col gap-4 px-2 pb-2'>
                    <div className='flex flex-col items-center'>
                        <DateTimePicker
                            setDateTimeValues={setDateTimeValues}
                            isLocalDateAndTime={isLocalDateAndTime}
                            isRestricted={isRestricted}
                            resetSelection={resetSelection}
                            allowRangeSelection
                            rangeDisabled={(date) => date >= new Date()}
                        />
                    </div>

                    <div className='flex gap-2 sticky bottom-0 bg-white pt-2'>
                        <Button entity='button.reset' onClick={() => setResetSelection(true)} variant='secondary' className='flex-1' />
                        <Button entity='button.submit' onClick={updateFilter} variant='primary' className='flex-1' disabled={!dateTimeValues?.startDateSelected} />
                    </div>
                </div>
            }
            contentClass='w-auto p-0 max-h-[75vh] overflow-y-auto'
        />
    );
}

export default DateFilter;

DateFilter.propTypes = {
    isLocalDateAndTime: PropTypes.bool,
    isRestricted: PropTypes.bool,
    addFilter: PropTypes.func.isRequired,
    columnId: PropTypes.string.isRequired,
    resetThisFilter: PropTypes.bool,
    setLaunchSearch: PropTypes.func.isRequired
};